import React, { useState, useEffect } from "react";
import { useAuth0 } from "react-auth0-spa";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  apiRequest,
  ApiCalls,
} from "../../forms/providerAppointmentForms/AllAppointmentsApi";
import DocumentViewer from "./DocumentViewer";
import "./AllDocuments.scss";

interface DocumentResponse {
  eventId: number;
  hasNote: boolean;
  noteStatus: string;
  noteId: number;
  startDate: string;
  appointmentTypeName: string;
  clientName: string;
  clientId: number;
}

interface Client {
  id: number;
  name: string;
}

interface ApiResponse {
  clients: Client[];
  documents: DocumentResponse[][];
}

interface Document {
  id: string;
  name: string;
  type: string;
  url?: string;
  date?: string;
  noteId?: number;
  eventId?: number;
  clientName?: string;
  clientId?: number;
}

interface LocationState {
  documents?: Document[];
  clients?: Client[];
  clientEmail?: string;
}

const AllDocuments: React.FC = () => {
  const { getTokenSilently, user } = useAuth0();
  const location = useLocation();
  const state = location.state as LocationState;

  const [documents, setDocuments] = useState<Document[]>(state?.documents || []);
  const [clients, setClients] = useState<Client[]>(state?.clients || []);
  const [loading, setLoading] = useState<boolean>(!state?.documents);
  const [error, setError] = useState<boolean>(false);

  const clientEmail = state?.clientEmail || user?.email;

  // Fetch documents if not passed via navigation state
  useEffect(() => {
    if (state?.documents && state?.clients) {
      return;
    }

    const fetchDocuments = async () => {
      if (!clientEmail) {
        setLoading(false);
        return;
      }

      try {
        const accessToken = await getTokenSilently();
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        };

        const response = await axios.post(
          `${apiRequest}/${ApiCalls.getAllApprovedBloomDocuments}`,
          { email: clientEmail },
          config
        );

        if (!response.data) {
          setDocuments([]);
          setClients([]);
          setError(false);
          setLoading(false);
          return;
        }

        // The API returns an array with a single object: [{ clients: [...], documents: [...] }]
        const rawData = Array.isArray(response.data) ? response.data[0] : response.data;
        const apiData = rawData as ApiResponse;

        if (!apiData) {
          setDocuments([]);
          setClients([]);
          setError(false);
          setLoading(false);
          return;
        }

        if (apiData.clients) {
          setClients(apiData.clients);
        }

        const allDocuments: Document[] = [];
        if (apiData.documents && Array.isArray(apiData.documents)) {
          apiData.documents.forEach((clientDocs: DocumentResponse[]) => {
            if (Array.isArray(clientDocs)) {
              clientDocs.forEach((doc: DocumentResponse) => {
                allDocuments.push({
                  id: doc.noteId.toString(),
                  name: doc.appointmentTypeName,
                  type: "PDF",
                  date: doc.startDate,
                  noteId: doc.noteId,
                  eventId: doc.eventId,
                  clientName: doc.clientName,
                  clientId: doc.clientId,
                });
              });
            }
          });
        }

        setDocuments(allDocuments);
        setError(false);
      } catch (err) {
        console.error("[AllDocuments] Error fetching documents:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [clientEmail, getTokenSilently, state?.documents, state?.clients]);

  return (
    <DocumentViewer
      documents={documents}
      clients={clients}
      loading={loading}
      error={error}
      isAllDocuments={true}
      showBreadcrumbs={true}
      clientEmail={clientEmail}
    />
  );
};

export default AllDocuments;

